import { BillingPriceInterval } from '@tribeplatform/gql-client/types/tribe-graphql'

export { BillingPriceInterval }

/** The slice of a billing price the offer rules read. */
export interface OfferablePrice {
  id: string
  interval: BillingPriceInterval
  /** Stripe `metadata.default === 'true'`: the price self-serve sells at this interval. */
  default: boolean
}

/**
 * The price a plan is offered at for an interval: the default price there, and only that.
 *
 * A plan can carry non-default prices at an interval (negotiated or sales-only). Those are
 * not offers — a plan with no default price at an interval is sales-led at that interval,
 * and the API refuses non-default prices in every purchase path. Never fall back to one.
 */
export const getOfferedPrice = <P extends OfferablePrice>(
  prices: readonly P[],
  interval: BillingPriceInterval,
): P | undefined =>
  prices.find(price => price.default && price.interval === interval)

/** Offered yearly, not offered monthly: self-serve annual, sales-led monthly. */
export const isAnnualOnly = (prices: readonly OfferablePrice[]): boolean =>
  getOfferedPrice(prices, BillingPriceInterval.Year) !== undefined &&
  getOfferedPrice(prices, BillingPriceInterval.Month) === undefined

/**
 * Which billing interval the plans page shows by default.
 *
 * An explicit query param always wins. Otherwise a customer sees their own plan's interval, but a
 * network with no plan defaults to Year — the recommended plan may be annual-only, so landing on
 * Month would show it as not purchasable. (`currentInterval` itself falls back to Month upstream,
 * which is why "no plan" cannot simply trust it.)
 */
export const resolveSelectedInterval = ({
  queryParam,
  hasPlan,
  currentInterval,
}: {
  queryParam: BillingPriceInterval | null | undefined
  hasPlan: boolean
  currentInterval: BillingPriceInterval
}): BillingPriceInterval =>
  queryParam ?? (hasPlan ? currentInterval : BillingPriceInterval.Year)
